import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { MAIN_REGISTRATION_FORM, MENU_ITEMS, NAV_LINKS } from "@/data/eventData";

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname, location.hash]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                data-testid="navbar"
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "border-b border-cream/5 bg-ink/70 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <nav className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 md:h-20 md:px-10">
                    <Link
                        to="/"
                        data-testid="navbar-logo"
                        className="flex items-baseline gap-2.5"
                    >
                        <span className="font-serif text-2xl font-medium tracking-tight text-cream">
                            I-ACE
                        </span>
                        <span className="font-sans text-[9px] font-semibold tracking-[0.4em] text-scarlet md:text-[10px]">
                            CHAPTER&nbsp;4
                        </span>
                    </Link>

                    <div className="hidden items-center gap-10 lg:flex">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-link-${link.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `group relative font-sans text-[11px] font-semibold uppercase tracking-[0.3em] transition-colors duration-300 ${
                                        isActive ? "text-cream" : "text-ash hover:text-cream"
                                    }`
                                }
                            >
                                {link.label}
                                <span className="absolute -bottom-1.5 left-0 h-px w-full origin-left scale-x-0 bg-scarlet transition-transform duration-300 group-hover:scale-x-100" />
                            </NavLink>
                        ))}
                    </div>

                    <div className="flex items-center gap-4">
                        <a
                            href={MAIN_REGISTRATION_FORM}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="navbar-register-btn"
                            className="group hidden items-center gap-2.5 bg-crimson px-6 py-3 font-sans text-[11px] font-semibold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-scarlet lg:inline-flex"
                        >
                            Register Now
                            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                        <button
                            type="button"
                            data-testid="mobile-menu-btn"
                            aria-label={open ? "Close menu" : "Open menu"}
                            onClick={() => setOpen((v) => !v)}
                            className="flex h-11 w-11 items-center justify-center text-cream transition-colors hover:text-scarlet lg:hidden"
                        >
                            {open ? <X className="h-6 w-6 text-scarlet" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </nav>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-40 flex flex-col justify-between bg-ink/95 px-6 pb-10 pt-28 backdrop-blur-xl lg:hidden"
                    >
                        <nav className="min-h-0 flex-1 overflow-y-auto">
                            {MENU_ITEMS.map((link, i) => {
                                const active =
                                    link.to === "/"
                                        ? location.pathname === "/" && !location.hash
                                        : link.to.startsWith("/#")
                                          ? location.pathname === "/" &&
                                            location.hash === link.to.slice(1)
                                          : location.pathname === link.to;
                                return (
                                    <motion.div
                                        key={link.to}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            delay: 0.06 + i * 0.05,
                                            duration: 0.45,
                                            ease: [0.16, 1, 0.3, 1],
                                        }}
                                    >
                                        <Link
                                            to={link.to}
                                            onClick={() => setOpen(false)}
                                            data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                            className={`flex items-baseline gap-4 border-b border-cream/5 py-3.5 transition-colors duration-300 ${
                                                active
                                                    ? "text-scarlet"
                                                    : "text-cream hover:text-scarlet"
                                            }`}
                                        >
                                            <span className="font-sans text-[10px] font-semibold tracking-[0.3em] text-scarlet/70">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span className="font-serif text-2xl font-light tracking-tight md:text-3xl">
                                                {link.label}
                                            </span>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </nav>
                        <motion.a
                            href={MAIN_REGISTRATION_FORM}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="mobile-register-btn"
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.35, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            className="group flex items-center justify-center gap-3 bg-crimson py-5 font-sans text-xs font-semibold uppercase tracking-[0.3em] text-cream transition-colors duration-300 hover:bg-scarlet"
                        >
                            Register Now
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </motion.a>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
